/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// หน้า "ปรับสเกลภาพ" (A7 · 25 ต.ค. 2569 · ลูกค้าเรียก "รีเกลป้ายสินค้า")
//   เฟส 1: อัปโหลด / เลือกจากระบบ → ติ๊กสเกลได้หลายแบบ → ครอบ (จุดโฟกัส) หรือ เติมพื้นหลัง → แปลงในเบราว์เซอร์ → ดาวน์โหลด/zip
//   เฟส 2: "ให้ AI ต่อภาพ" (outpaint คงสินค้า/ข้อความ · ใช้เครดิต · backend controllers/image_scale.con.php)
//          + "เก็บเข้า" แกลเลอรีสินค้า/รูปแคมเปญ เมื่อรูปต้นทางมาจากระบบ
//   ตรรกะวาดอยู่ _entrio/imageScale.js · เลือกจากระบบ _entrio/SystemImagePicker.jsx · เมนู migration 0033
import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDProgress from 'components/MDProgress';
import MDSnackbar from 'components/MDSnackbar';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import zipStore from 'layouts/_entrio/zipStore';
import {
  saveBlob,
  safeName,
  fetchImageBytes,
  extOf,
  downloadZip,
  zipDoneText,
} from 'layouts/_entrio/imageDownload';
import SystemImagePicker from 'layouts/_entrio/SystemImagePicker';
import {
  RATIOS,
  LONG_SIDES,
  MAX_SOURCES,
  loadSource,
  cropRect,
  targetSize,
  renderScaled,
  canvasToBlob,
  ratioTag,
  baseName,
  blobToDataUrl,
  sourceDataUrl,
} from 'layouts/_entrio/imageScale';

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(
    d.getDate()
  ).padStart(2, '0')}`;
};
const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
const POLL_MS = 6000; // preventDuplicate ของ backend ปัดคำขอซ้ำใน 5 วิ
const NOFILE_MAX = 10; // "สำเร็จแต่ดึงไฟล์ไม่ได้" ติดกัน 10 รอบ (~1 นาที) = หยุดรอ
const ZIP_MAX = 314572800; // 300 MB — zip รวมในเบราว์เซอร์ เพดานเดียวกับหน้าดาวน์โหลดรูป
const AI_ERROR = {
  permission_denied: 'บัญชีนี้ไม่มีสิทธิ์สร้างภาพ AI',
  ai_kill_switch_on: 'AI ปิดทั้งร้านอยู่ (หยุดฉุกเฉิน) — ผู้ดูแลระบบเปิดได้ที่หน้า AI ค่าใช้จ่าย',
  ai_quota_not_set: 'AI ปิดทั้งร้านอยู่ (โควตาเป็น 0) — ผู้ดูแลระบบตั้งได้ที่หน้า AI ค่าใช้จ่าย',
  ai_quota_exceeded: 'โควตาภาพ AI เดือนนี้เต็มแล้ว',
  ai_user_quota_exceeded: 'เครดิต AI เดือนนี้ของคุณครบตามที่ผู้ดูแลตั้งไว้แล้ว — ติดต่อผู้ดูแลระบบ',
  ai_ratio_not_supported: 'AI ไม่รองรับสเกลกระดาษ A3/A4/A5 — ใช้ครอบหรือเติมพื้นหลังแทน',
  ai_source_data_invalid: 'ส่งรูปให้ AI ไม่ได้ (ใช้ JPG/PNG)',
  ai_provider_not_found: 'ร้านยังไม่ได้เชื่อมต่อผู้ให้บริการ AI — แจ้งไอที',
  ai_create_failed: 'ผู้ให้บริการ AI ไม่รับงาน ลองใหม่อีกครั้ง',
  target_not_found: 'ไม่พบสินค้า/แคมเปญปลายทางแล้ว',
  ai_source_file_too_large: 'รูปใหญ่เกินไป ย่อรูปก่อนแล้วลองใหม่',
  ai_upload_failed: 'ส่งรูปให้ผู้ให้บริการ AI ไม่สำเร็จ ลองใหม่อีกครั้ง',
  ai_api_key_missing: 'ร้านยังไม่ได้เชื่อมต่อผู้ให้บริการ AI — แจ้งไอที',
  not_owner: 'งานนี้เป็นของคนอื่น',
  ai_result_not_ready: 'ภาพยังไม่พร้อม ลองใหม่ภายหลัง',
  ai_copy_failed: 'บันทึกไฟล์ภาพไม่สำเร็จ แจ้งไอที',
  poll_failed: 'ตรวจสถานะกับผู้ให้บริการ AI ไม่ได้',
};
const POLL_FAIL_MAX = 5; // ตรวจสถานะล้มติดกันกี่รอบแล้วหยุด (เช่นหลุดล็อกอิน · รีเช็ค 27 ต.ค. 2569)
const NET_ERROR = 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง';
const creditOf = (p, res) => {
  if (!p) return 0;
  const by = p.credits || {};
  return Number(by[res] || p.credits_per_image || 0);
};

function ImageScale() {
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const mod = 'image_scale';
  const alive = useRef(true);
  const toastTimer = useRef(null);
  const fileRef = useRef(null);
  const boxRef = useRef(null);
  const dragging = useRef(false);
  const previewRef = useRef(null);
  const pollTimer = useRef(null);
  const pollFails = useRef(0);

  const [sources, setSources] = useState([]); // { id, name, img, blob, objectUrl, width, height, focus, from }
  const [currentId, setCurrentId] = useState('');
  const [ratios, setRatios] = useState(['1:1']);
  const [mode, setMode] = useState('crop'); // crop | fit | ai
  const [bg, setBg] = useState('white'); // white | color | blur
  const [color, setColor] = useState('#ffffff');
  const [longSide, setLongSide] = useState('1080');
  const [format, setFormat] = useState('jpg');
  const [results, setResults] = useState([]); // { key, sourceId, name, ratioKey, ext, blob, url, w, h }
  const [progress, setProgress] = useState(null);
  const [loadingSrc, setLoadingSrc] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [notify, setNotify] = useState(null);
  /* เฟส 2 */
  const [ai, setAi] = useState(null); // init ของ /image_scale
  const [resolution, setResolution] = useState('1K');
  const [provider, setProvider] = useState('');
  const [aiJobs, setAiJobs] = useState([]); // { id, sourceId, name, from, ratioKey, state, url, w, h, error, credits }
  const [aiConfirm, setAiConfirm] = useState(false);
  const [aiSubmitting, setAiSubmitting] = useState(false);
  const [saving, setSaving] = useState(''); // key ของผลที่กำลังเก็บ
  const [savedKeys, setSavedKeys] = useState({}); // ผลที่เก็บเข้าระบบแล้ว (กันเก็บซ้ำ)
  const [reorderFrom, setReorderFrom] = useState(0); // กด "สั่งชุดนี้อีกรอบ" = ไม่นับงานที่สั่งก่อนเวลานี้
  const pendingRef = useRef([]); // id งาน AI ที่ยังไม่จบ — poll จากตัวนี้ (สั่งหลายรอบซ้อนกันได้)
  const noFileRef = useRef({}); // id → จำนวนรอบที่ "สำเร็จแต่ยังไม่มีไฟล์"

  const showNotify = (msg, c) => {
    setNotify(
      <MDSnackbar
        color={c}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        open
        onClose={() => setNotify(null)}
        close={() => setNotify(null)}
        bgWhite
      />
    );
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(
      () => {
        if (alive.current) setNotify(null);
      },
      c === 'error' ? 8000 : 3000
    );
  };
  /* ผลลัพธ์ผูกกับค่าที่ตั้งตอนแปลง — ค่าเปลี่ยนเมื่อไรล้างทิ้ง (คืน object URL) ไม่ให้ผู้ใช้โหลดของเก่าโดยไม่รู้ */
  /* ผลในเครื่องรอบใหม่ = ภาพใหม่ — ล้างเครื่องหมาย "เก็บแล้ว" ของรอบก่อนด้วย (รีเช็ค 26 ต.ค.: เดิมแก้ครอบแล้วเก็บซ้ำไม่ได้)
     ของงาน AI (key ขึ้นต้น ai) คงไว้ */
  const forgetSaved = (keep) =>
    setSavedKeys((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([k]) => k.startsWith('ai') || keep(k)))
    );
  const clearResults = () => {
    forgetSaved(() => false);
    setResults((prev) => {
      prev.forEach((r) => URL.revokeObjectURL(r.url));
      return [];
    });
  };

  /* คืนหน่วยความจำของ object URL ทุกตัวตอนออกจากหน้า */
  const urlsRef = useRef([]);
  useEffect(() => {
    urlsRef.current = [...sources.map((s) => s.objectUrl), ...results.map((r) => r.url)];
  }, [sources, results]);
  useEffect(() => () => urlsRef.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const aiOn = ai && ai.can_generate === 'Y' && ai.ai_ready === 'Y';
  const aiRatios = (ai && ai.ai_ratios) || [];
  const current = sources.find((s) => s.id === currentId) || sources[0] || null;
  const selectedRatios = RATIOS.filter(
    (r) => ratios.includes(r.key) && (mode !== 'ai' || aiRatios.includes(r.key))
  );
  const previewRatio = selectedRatios[0] || RATIOS[0];
  const longSideValue = (LONG_SIDES.find((l) => l.key === longSide) || LONG_SIDES[0]).value;
  const ext = format === 'png' ? 'png' : 'jpg';
  const providerRow = ai && (ai.providers || []).find((p) => p.code === provider);
  const creditEach = creditOf(providerRow, resolution);
  const busy = Boolean(progress) || aiSubmitting;

  /* ── เพิ่มรูป ── */
  const addSources = async (items) => {
    // items: [{ file }] หรือ [{ url, name, from }]
    const room = MAX_SOURCES - sources.length;
    if (room <= 0) {
      showNotify(`ใส่ได้ไม่เกิน ${MAX_SOURCES} รูปต่อครั้ง`, 'warning');
      return;
    }
    const take = items.slice(0, room);
    if (take.length < items.length)
      showNotify(`ใส่ได้ไม่เกิน ${MAX_SOURCES} รูปต่อครั้ง`, 'warning');
    setLoadingSrc(true);
    const added = [];
    let failed = 0;
    const used = new Set(sources.map((x) => x.name));
    const unique = (raw) => {
      let name = raw || 'image';
      let n = 2;
      while (used.has(name)) {
        name = `${raw}_${n}`;
        n += 1;
      }
      used.add(name);
      return name;
    };
    for (let i = 0; i < take.length; i += 1) {
      const it = take[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        const s = await loadSource(it.file || it.url);
        added.push({
          id: uid(),
          name: unique(baseName(it.file ? it.file.name : it.name)), // ชื่อซ้ำ = ไฟล์ใน zip ชนกัน
          img: s.img,
          blob: s.blob,
          objectUrl: s.objectUrl,
          width: s.width,
          height: s.height,
          focus: { x: 0.5, y: 0.5 },
          from: it.from || null,
        });
      } catch (e) {
        failed += 1;
      }
    }
    if (!alive.current) {
      added.forEach((a) => URL.revokeObjectURL(a.objectUrl));
      return;
    }
    setLoadingSrc(false);
    if (added.length) {
      setSources((prev) => [...prev, ...added]);
      setCurrentId((cur) => cur || added[0].id);
      clearResults();
    }
    if (failed) showNotify(`เปิดรูปไม่ได้ ${failed} ไฟล์ (ใช้ JPG/PNG/WebP)`, 'error');
  };
  const onFiles = (e) => {
    const files = [...(e.target.files || [])].filter((f) => /^image\//.test(f.type));
    e.target.value = '';
    if (!files.length) {
      showNotify('เลือกไฟล์รูปภาพ (JPG/PNG/WebP)', 'warning');
      return;
    }
    addSources(files.map((file) => ({ file })));
  };
  const removeSource = (id) => {
    setSources((prev) => {
      const s = prev.find((x) => x.id === id);
      if (s) URL.revokeObjectURL(s.objectUrl);
      return prev.filter((x) => x.id !== id);
    });
    setResults((prev) => {
      prev.filter((r) => r.sourceId === id).forEach((r) => URL.revokeObjectURL(r.url));
      return prev.filter((r) => r.sourceId !== id);
    });
    if (currentId === id) setCurrentId('');
  };

  /* ── จุดโฟกัสของการครอบ (ลาก/กด/ลูกศร) ── */
  const dropResultsOf = (sourceId) => {
    forgetSaved((k) => !k.startsWith(`${sourceId}_`));
    setResults((prev) => {
      prev.filter((r) => r.sourceId === sourceId).forEach((r) => URL.revokeObjectURL(r.url));
      return prev.filter((r) => r.sourceId !== sourceId); // ผลเก่าของรูปนี้ไม่ตรงแล้ว
    });
  };
  const setFocusFromEvent = (e) => {
    if (!current || mode !== 'crop' || !boxRef.current) return;
    const rect = boxRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    setSources((prev) => prev.map((s) => (s.id === current.id ? { ...s, focus: { x, y } } : s)));
    dropResultsOf(current.id);
  };
  const onPointerDown = (e) => {
    if (busy) return; // ระหว่างแปลง ผลใช้จุดโฟกัสตอนกดแปลง — ห้ามเลื่อนให้กรอบบนจอไม่ตรงกับผล
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    setFocusFromEvent(e);
  };
  const onPointerMove = (e) => dragging.current && setFocusFromEvent(e);
  const onPointerUp = (e) => {
    if (dragging.current) e.currentTarget.releasePointerCapture(e.pointerId);
    dragging.current = false;
  };
  const nudgeFocus = (e) => {
    if (!current || mode !== 'crop' || busy) return; // ระหว่างแปลงห้ามเลื่อน (เหมือนเมาส์)
    const d = {
      ArrowLeft: [-0.02, 0],
      ArrowRight: [0.02, 0],
      ArrowUp: [0, -0.02],
      ArrowDown: [0, 0.02],
    }[e.key];
    if (!d) return;
    e.preventDefault();
    const x = Math.max(0, Math.min(1, current.focus.x + d[0]));
    const y = Math.max(0, Math.min(1, current.focus.y + d[1]));
    setSources((prev) => prev.map((s) => (s.id === current.id ? { ...s, focus: { x, y } } : s)));
    dropResultsOf(current.id);
  };

  /* ── ตัวอย่างผลลัพธ์เล็ก ๆ ของสเกลแรกที่ติ๊ก (ครอบ/เติม · โหมด AI โชว์ต้นฉบับ) ── */
  useEffect(() => {
    const el = previewRef.current;
    if (!el || !current || mode === 'ai') return;
    const c = renderScaled(current.img, previewRatio, {
      mode,
      focus: current.focus,
      bg,
      color,
      longSide: 360,
      flatten: format !== 'png',
    });
    el.width = c.width;
    el.height = c.height;
    el.getContext('2d').drawImage(c, 0, 0);
    c.width = 0; // คืนหน่วยความจำ canvas ชั่วคราวทันที — ลากจุดโฟกัสสร้างใหม่ทุกครั้ง (iOS Safari จำกัดรวม)
    c.height = 0;
  }, [current, previewRatio, mode, bg, color, format, sources]);

  /* ── แปลงในเครื่อง (ครอบ / เติมพื้นหลัง) ── */
  const convert = async () => {
    if (!sources.length || !selectedRatios.length || busy) return;
    /* ประเมินขนาดไฟล์รวมก่อนแปลง — "ขนาดพิมพ์" ทำให้ผลลัพธ์ 4096px เป็นเรื่องปกติ ภาพหลายร้อย MB ค้างในหน่วยความจำ
       แท็บเล็ตค้างก่อนถึงด่าน zip 300 MB (รีวิว 29 ต.ค.) · JPG ~0.4 ไบต์/พิกเซล · PNG ~2 */
    const perPx = format === 'png' ? 2 : 0.4;
    const est = sources.reduce(
      (sum, s) =>
        sum +
        selectedRatios.reduce((a, r) => {
          const crop = mode === 'crop' ? cropRect(r, s.width, s.height, s.focus) : null;
          const { w, h } = targetSize(r, s.width, s.height, longSideValue, mode, crop);
          return a + w * h * perPx;
        }, 0),
      0
    );
    if (est > ZIP_MAX) {
      showNotify(
        `ผลลัพธ์รวมราว ${Math.round(
          est / 1048576
        )} MB เกิน 300 MB ที่เบราว์เซอร์รับไหว · ลดจำนวนรูปหรือสเกล หรือเลือกขนาดผลลัพธ์เล็กลง`,
        'error'
      );
      return;
    }
    clearResults();
    const total = sources.length * selectedRatios.length;
    setProgress([0, total]);
    const out = [];
    const failed = [];
    let n = 0;
    for (let i = 0; i < sources.length; i += 1) {
      for (let j = 0; j < selectedRatios.length; j += 1) {
        const s = sources[i];
        const r = selectedRatios[j];
        try {
          const canvas = renderScaled(s.img, r, {
            mode,
            focus: s.focus,
            bg,
            color,
            longSide: longSideValue,
            flatten: format !== 'png',
          });
          const w = canvas.width;
          const h = canvas.height;
          /* ภาพถูกขยายกี่เท่าจากพิกเซลจริง (ครอบ = เทียบกรอบ · เติมพื้นหลัง = เทียบภาพเดิม) — เกิน 1.5 เท่าเตือนว่าอาจไม่คมเมื่อพิมพ์ */
          const crop = mode === 'crop' ? cropRect(r, s.width, s.height, s.focus) : null;
          const up = crop ? w / crop.w : Math.min(w / s.width, h / s.height);
          // eslint-disable-next-line no-await-in-loop
          const blob = await canvasToBlob(canvas, format);
          out.push({
            key: `${s.id}_${r.key}`,
            sourceId: s.id,
            name: s.name,
            ratioKey: r.key,
            ext,
            blob,
            url: URL.createObjectURL(blob),
            w,
            h,
            up,
          });
        } catch (e) {
          failed.push(`${s.name} (${r.key})`);
        }
        n += 1;
        if (!alive.current) {
          out.forEach((x) => URL.revokeObjectURL(x.url));
          return;
        }
        setProgress([n, total]);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 0)); // ให้หน้าจอขยับระหว่างแปลงหลายรูป
      }
    }
    if (!alive.current) {
      out.forEach((x) => URL.revokeObjectURL(x.url));
      return;
    }
    setResults(out);
    setProgress(null);
    if (failed.length)
      showNotify(
        `แปลงเสร็จ ${out.length}/${total} · ไม่สำเร็จ: ${failed.slice(0, 3).join(', ')}${
          failed.length > 3 ? ` และอีก ${failed.length - 3}` : ''
        } — รูปอาจใหญ่เกินหน่วยความจำ ลองลดขนาดผลลัพธ์`,
        'error'
      );
    else showNotify(`แปลงเสร็จ ${out.length} ภาพ`, 'success');
  };
  const fileNameOf = (r) => `${safeName(r.name)}_${ratioTag(r.ratioKey)}.${r.ext || ext}`;
  const downloadOne = (r) => saveBlob(r.blob, fileNameOf(r));
  const downloadAllRun = async () => {
    /* รวม zip ในเบราว์เซอร์ — ใหญ่เกินเครื่องค้าง (เพดานเดียวกับหน้าดาวน์โหลดรูป · รีเช็ค 26 ต.ค.) */
    const bytesAll = results.reduce((a, r) => a + (r.blob ? r.blob.size : 0), 0);
    if (bytesAll > ZIP_MAX) {
      showNotify(
        `ผลรวม ${Math.round(bytesAll / 1048576)} MB ใหญ่เกิน zip เดียว (ไม่เกิน ${Math.round(
          ZIP_MAX / 1048576
        )} MB) — เลือกขนาด 1080/2048 หรือ JPG หรือโหลดทีละภาพ`,
        'warning'
      );
      return;
    }
    const many = new Set(results.map((r) => r.sourceId)).size > 1;
    const files = [];
    for (let i = 0; i < results.length; i += 1) {
      const r = results[i];
      // eslint-disable-next-line no-await-in-loop
      const bytes = new Uint8Array(await r.blob.arrayBuffer());
      files.push({ name: (many ? `${safeName(r.name)}/` : '') + fileNameOf(r), data: bytes });
    }
    saveBlob(zipStore(files), `ปรับสเกล_${today()}.zip`);
  };
  const zipBusy = useRef(false); // กดซ้ำระหว่างรวม zip = ได้ 2 ไฟล์ (รีเช็ค 27 ต.ค. 2569)
  const downloadAll = async () => {
    if (!results.length || zipBusy.current) return;
    zipBusy.current = true;
    try {
      await downloadAllRun();
    } finally {
      zipBusy.current = false;
    }
  };

  /* ── เฟส 2: AI ต่อภาพ ── */
  /* คู่ รูป × สเกล ที่ยังไม่ได้สั่งในรอบนี้ — กดสั่งซ้ำได้งานที่เหลือ ไม่ใช่ 6 งานแรกซ้ำ (รีเช็ค 26 ต.ค.: เดิมเสียเครดิตซ้ำ
     และงานที่ 7 ขึ้นไปสั่งไม่ได้เลย) · งานที่ไม่สำเร็จนับเป็นยังไม่สั่ง สั่งใหม่ได้ */
  /* นับเฉพาะงานความละเอียด/โมเดลเดียวกัน และหลังกด "สั่งชุดนี้อีกรอบ" — เปลี่ยน 2K/โมเดล หรืออยากได้ภาพใหม่ สั่งได้ (ผู้ตรวจรอบแก้ 26 ต.ค.) */
  const aiPlan = () => {
    const ordered = new Set(
      aiJobs
        .filter((j) => j.state !== 'FAILED' && j.sourceId && (j.at0 || 0) >= reorderFrom)
        .map((j) => `${j.sourceId}|${j.ratioKey}|${j.res}|${j.prov}`)
    );
    const jobs = [];
    sources.forEach((s) =>
      selectedRatios.forEach((r) => {
        if (!ordered.has(`${s.id}|${r.key}|${resolution}|${provider}`)) jobs.push({ s, r });
      })
    );
    return jobs;
  };
  const stopPoll = () => {
    clearTimeout(pollTimer.current);
    pollTimer.current = null;
  };
  const pollAi = (ids) => {
    /* รวม id ค้างทุกชุด — สั่งรอบสองระหว่างรอบแรกยังไม่เสร็จ ต้องไม่หยุด poll รอบแรก */
    pendingRef.current = [...new Set([...pendingRef.current, ...ids])].slice(0, 24);
    stopPoll();
    if (!pendingRef.current.length) return;
    pollTimer.current = setTimeout(() => {
      const batch = pendingRef.current.slice();
      FetchData('OPTIONS', `/${mod}/ai_status`, app.token, { ids: batch, _t: Date.now() })
        .then(async (r) => {
          if (!alive.current) return;
          if (r.status !== 'ok') {
            pollFails.current += 1;
            if (pollFails.current >= POLL_FAIL_MAX) {
              showNotify('ตรวจสถานะงาน AI ไม่ได้ — โหลดหน้านี้ใหม่เพื่อดูผล', 'error');
              return;
            }
            pollAi([]);
            return;
          }
          pollFails.current = 0;
          const byId = {};
          (r.jobs || []).forEach((j) => {
            byId[j.id] = j;
          });
          /* อ่านขนาดภาพที่เสร็จ */
          const dims = {};
          await Promise.all(
            (r.jobs || [])
              .filter((j) => j.state === 'SUCCESS' && j.image_url)
              .map(
                (j) =>
                  new Promise((res) => {
                    const im = new Image();
                    im.onload = () => {
                      dims[j.id] = [im.naturalWidth, im.naturalHeight];
                      res();
                    };
                    im.onerror = res;
                    im.src = j.image_url;
                  })
              )
          );
          if (!alive.current) return;
          const doneNow = batch.filter(
            (id) => byId[id] && byId[id].state === 'SUCCESS' && byId[id].image_url
          ).length;
          const failNow = batch.filter(
            (id) => byId[id] && ['FAILED', 'CANCELLED'].includes(byId[id].state)
          ).length;
          /* ยังไม่จบ = ยังไม่ตอบ · ยังทำอยู่ · หรือ "สำเร็จ" แต่เซิร์ฟเวอร์ยังโหลดไฟล์ไม่ได้ (ไม่มี image_url) — poll ต่อ
             รีเช็ค 26 ต.ค.: เดิมกรณีหลังกลายเป็นการ์ดภาพเสีย ปุ่มดาวน์โหลดได้หน้า index.html */
          /* ดึงไฟล์ไม่ได้ติดกันเกิน NOFILE_MAX รอบ (~1 นาที) = หยุดรอ บอกให้โหลดหน้าใหม่ภายหลัง (ผู้ตรวจรอบแก้ 26 ต.ค.: เดิมรอไม่จบ) */
          const noFile = {};
          batch.forEach((id) => {
            const u = byId[id];
            if (u && u.state === 'SUCCESS' && !u.image_url) {
              noFileRef.current[id] = (noFileRef.current[id] || 0) + 1;
              if (noFileRef.current[id] >= NOFILE_MAX) noFile[id] = true;
            }
          });
          const stillPending = batch.filter((id) => {
            const u = byId[id];
            if (!u) return true;
            if (u.state === 'SUCCESS') return !u.image_url && !noFile[id];
            return !['FAILED', 'CANCELLED'].includes(u.state);
          });
          setAiJobs((prev) =>
            prev.map((j) => {
              const u = byId[j.id];
              if (!u || j.state === 'SUCCESS' || j.state === 'FAILED') return j;
              if (u.state === 'SUCCESS' && u.image_url)
                return {
                  ...j,
                  state: 'SUCCESS',
                  url: u.image_url,
                  w: (dims[j.id] || [0, 0])[0],
                  h: (dims[j.id] || [0, 0])[1],
                  credits: u.credits,
                };
              if (u.state === 'FAILED' || u.state === 'CANCELLED')
                return { ...j, state: 'FAILED', error: AI_ERROR[u.error] || u.error || '' };
              if (u.state === 'SUCCESS' && noFile[j.id])
                return {
                  ...j,
                  state: 'FAILED',
                  error:
                    'AI ทำเสร็จแล้ว (คิดเครดิตแล้ว) แต่ดึงไฟล์ภาพไม่ได้ — โหลดหน้านี้ใหม่ภายหลังเพื่อลองอีกครั้ง',
                };
              if (u.state === 'SUCCESS') return { ...j, state: 'RUNNING' }; // รอเซิร์ฟเวอร์โหลดไฟล์
              return { ...j, state: u.state || 'RUNNING' };
            })
          );
          /* id ที่สั่งเพิ่มระหว่างคำขอนี้ยังรออยู่ — ห้ามทับทิ้ง (รีเช็ค 26 ต.ค.: งานรอบสองค้างหมุนไม่จบ) */
          pendingRef.current = [
            ...stillPending,
            ...pendingRef.current.filter((id) => !batch.includes(id)),
          ].slice(0, 24);
          if (doneNow) showNotify(`AI ต่อภาพเสร็จ ${doneNow} ภาพ`, 'success');
          if (failNow) showNotify(`AI ต่อภาพไม่สำเร็จ ${failNow} ภาพ — ไม่หักเครดิต`, 'error');
          if (pendingRef.current.length) pollAi([]);
        })
        .catch(() => {
          if (!alive.current) return;
          pollFails.current += 1;
          if (pollFails.current >= POLL_FAIL_MAX) {
            showNotify('ตรวจสถานะงาน AI ไม่ได้ — โหลดหน้านี้ใหม่เพื่อดูผล', 'error');
            return;
          }
          pollAi([]);
        });
    }, POLL_MS);
  };
  /* งานของตัวเอง 3 วันล่าสุด — เครดิตจ่ายแล้ว โหลดหน้าใหม่ต้องยังหาผลเจอ */
  const loadRecent = () => {
    FetchData('OPTIONS', `/${mod}/recent`, app.token, { _t: Date.now() })
      .then((r) => {
        if (!alive.current || r.status !== 'ok') return;
        const jobs = (r.jobs || []).map((j) => ({
          id: Number(j.id),
          sourceId: '',
          name: j.name || `งาน ${j.id}`,
          from:
            j.from && j.from.id
              ? { kind: j.from.kind, id: Number(j.from.id), code: j.from.code }
              : null,
          ratioKey: j.aspect_ratio || '',
          state: j.state === 'SUCCESS' && !j.image_url ? 'RUNNING' : j.state,
          url: j.image_url || '',
          w: 0,
          h: 0,
          error: j.error || '',
          credits: Number(j.credits || 0),
          at: j.created_at,
        }));
        setAiJobs((prev) => {
          const have = new Set(prev.map((x) => x.id));
          return [...prev, ...jobs.filter((j) => !have.has(j.id))];
        });
        const pending = jobs
          .filter((j) => !['SUCCESS', 'FAILED', 'CANCELLED'].includes(j.state))
          .map((j) => j.id);
        if (pending.length) pollAi(pending);
      })
      .catch(() => {});
  };
  /* โหลดครั้งแรก — อยู่หลัง pollAi/loadRecent เพราะต้องเรียกใช้ */
  useEffect(() => {
    alive.current = true;
    i18n.changeLanguage(app.lang);
    FetchData('GET', `/${mod}`, app.token, {})
      .then((r) => {
        if (!alive.current || r.status !== 'ok') return;
        setAi(r);
        const def = (r.providers || []).find((p) => p.is_default === 'Y') || (r.providers || [])[0];
        if (def) setProvider(def.code);
        if (r.can_generate === 'Y') loadRecent();
      })
      .catch(() => {});
    return () => {
      alive.current = false;
      clearTimeout(toastTimer.current);
      clearTimeout(pollTimer.current);
    };
  }, []);
  const submitAi = async () => {
    setAiConfirm(false);
    const plan = aiPlan().slice(0, (ai && ai.max_ai_jobs) || 6);
    if (!plan.length || aiSubmitting) return;
    setAiSubmitting(true);
    const made = [];
    let stop = '';
    for (let i = 0; i < plan.length && !stop; i += 1) {
      const { s, r } = plan[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        const filedata = await sourceDataUrl(s);
        // eslint-disable-next-line no-await-in-loop
        const res = await FetchData('OPTIONS', `/${mod}/ai_extend`, app.token, {
          filedata,
          name: s.name,
          aspect_ratio: r.key,
          resolution,
          provider_code: provider,
          from: s.from ? { kind: s.from.kind, id: s.from.id } : null, // จำไว้ เก็บกลับได้แม้โหลดหน้าใหม่
          _t: Date.now(),
        });
        if (res.status === 'ok' && res.id) {
          made.push({
            id: Number(res.id),
            sourceId: s.id,
            name: s.name,
            from: s.from,
            ratioKey: r.key,
            res: resolution,
            prov: provider,
            at0: Date.now(),
            state: 'PENDING',
            url: '',
            w: 0,
            h: 0,
            error: '',
            credits: 0,
          });
        } else stop = AI_ERROR[res.msg] || `สั่งงาน AI ไม่สำเร็จ (${res.msg || 'ไม่ทราบสาเหตุ'})`;
      } catch (e) {
        stop = NET_ERROR;
      }
    }
    if (!alive.current) return;
    setAiSubmitting(false);
    if (made.length) {
      setAiJobs((prev) => [...made, ...prev]);
      showNotify(
        `สั่ง AI แล้ว ${made.length} งาน · รอ 2–5 นาทีต่อภาพ · ปิดหน้าได้ กลับมาดูผลได้ที่หน้านี้ภายใน 3 วัน`,
        'success'
      );
      pollAi(made.map((j) => j.id));
    }
    if (stop)
      showNotify(
        made.length ? `${stop} — สั่งได้ ${made.length}/${plan.length} งาน` : stop,
        'error'
      );
  };
  const aiFileName = (j) => `${safeName(j.name)}_${ratioTag(j.ratioKey)}_ai`;
  const downloadAiOne = async (j) => {
    try {
      const { bytes, type } = await fetchImageBytes(j.url);
      saveBlob(new Blob([bytes], { type }), `${aiFileName(j)}.${extOf(j.url, type)}`);
    } catch (e) {
      showNotify('ดาวน์โหลดไม่สำเร็จ ลองอีกครั้ง', 'error');
    }
  };
  const downloadAiAllRun = async (done) => {
    try {
      /* ไฟล์ที่โหลดไม่ได้ข้ามไปพร้อมบอกชื่อ ไม่ทิ้งทั้ง zip (รีเช็ค 26 ต.ค.) */
      const res = await downloadZip(
        done.map((j) => ({ url: j.url, name: aiFileName(j) })),
        `ปรับสเกล_AI_${today()}`
      );
      if (alive.current) showNotify(zipDoneText(res), res.skipped.length ? 'warning' : 'success');
    } catch (e) {
      if (alive.current) showNotify('ดาวน์โหลดไม่สำเร็จ ลองอีกครั้ง', 'error');
    }
  };
  const downloadAiAll = async () => {
    const done = aiJobs.filter((j) => j.state === 'SUCCESS' && j.url);
    if (!done.length || zipBusy.current) return;
    zipBusy.current = true;
    try {
      await downloadAiAllRun(done);
    } finally {
      zipBusy.current = false;
    }
  };

  /* ── เฟส 2: เก็บกลับเข้าแกลเลอรีสินค้า / รูปแคมเปญ (เฉพาะรูปที่มาจากระบบ) ── */
  const MAX_SEND = 7000000; // ตัวอักษร data URL ≈ 5 MB ไฟล์ · post_max_size ของ PHP ตั้งต้น 8M
  const shrinkForSend = async (blob) => {
    /* ผลลัพธ์ PNG/ไฟล์ใหญ่ → JPEG 0.85 ด้านยาว ≤ 2048 ให้ส่งผ่าน 8M ได้ */
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = URL.createObjectURL(blob);
    });
    const scale = Math.min(1, 2048 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(img.naturalWidth * scale));
    c.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src);
    const out = await new Promise((res) => c.toBlob(res, 'image/jpeg', 0.85));
    c.width = 0;
    c.height = 0;
    return out;
  };
  const saveBack = async (key, from, name, payloadOf) => {
    if (!from || saving || savedKeys[key]) return;
    setSaving(key);
    try {
      const body = await payloadOf();
      if (body.filedata && body.filedata.length > MAX_SEND) {
        showNotify('ไฟล์ใหญ่เกินกว่าจะส่งเข้าระบบ — ย่อเป็น 2048px JPG แล้วส่งแทน', 'warning');
        body.filedata = await blobToDataUrl(await shrinkForSend(body.blob));
      }
      delete body.blob;
      const r = await FetchData('OPTIONS', `/${mod}/save`, app.token, {
        target: from.kind,
        id: from.id,
        name,
        ...body,
        _t: Date.now(),
      });
      if (!alive.current) return;
      if (r.status === 'ok') {
        setSavedKeys((prev) => ({ ...prev, [key]: true }));
        showNotify(
          `เก็บเข้า${from.kind === 'product' ? 'แกลเลอรีสินค้า' : 'รูปแคมเปญ'} ${from.code} แล้ว${
            r.main_set === 'Y' ? ' (ตั้งเป็นรูปหลัก เพราะยังไม่มี)' : ''
          }`,
          'success'
        );
      } else
        showNotify(
          AI_ERROR[r.msg] ||
            (r.msg === 'body_too_large'
              ? 'ไฟล์ใหญ่เกินที่เซิร์ฟเวอร์รับ — เลือก JPG หรือขนาดเล็กลง'
              : 'เก็บไม่สำเร็จ ลองใหม่อีกครั้ง'),
          'error'
        );
    } catch (e) {
      if (alive.current) showNotify(NET_ERROR, 'error');
    } finally {
      if (alive.current) setSaving('');
    }
  };
  const fromOf = (sourceId) => {
    const s = sources.find((x) => x.id === sourceId);
    return s ? s.from : null;
  };
  const saveLabel = (from) =>
    from.kind === 'product' ? `เก็บเข้าแกลเลอรี ${from.code}` : `เก็บเข้ารูปแคมเปญ ${from.code}`;

  const toggleRatio = (key) => {
    setRatios((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    clearResults();
  };
  const changeMode = (v) => {
    setMode(v);
    clearResults();
  };
  const changeBg = (v) => {
    setBg(v);
    clearResults();
  };
  const changeColor = (v) => {
    setColor(v);
    clearResults();
  };
  const changeLongSide = (v) => {
    setLongSide(v);
    clearResults();
  };
  const changeFormat = (v) => {
    setFormat(v);
    clearResults();
  };
  const crop =
    current && mode === 'crop'
      ? cropRect(previewRatio, current.width, current.height, current.focus)
      : null;
  const total = sources.length * selectedRatios.length;
  const aiTodo = mode === 'ai' ? aiPlan().length : 0; // คู่ที่ยังไม่ได้สั่ง
  /* เก็บเข้าแกลเลอรี/รูปแคมเปญ ต้องมีสิทธิ์ ai_generate (server ตรวจ) — บัญชีไม่มีสิทธิ์ไม่แสดงปุ่ม (รีเช็ค 26 ต.ค.) */
  const canSave = Boolean(ai && ai.can_generate === 'Y');
  const aiCount = Math.min(aiTodo, (ai && ai.max_ai_jobs) || 6);
  const aiCredits = aiCount * creditEach;
  const pendingAi = aiJobs.filter((j) => j.state !== 'SUCCESS' && j.state !== 'FAILED').length;

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.product`), 'ปรับสเกลภาพ']} />
      <MDBox pt={2} pb={3}>
        <MDBox mb={2}>
          <MDTypography variant='h4' fontWeight='bold'>
            ปรับสเกลภาพ
          </MDTypography>
          <MDTypography variant='button' color='text'>
            ใส่รูปแล้วเลือกสเกลที่ต้องการ กดแปลงครั้งเดียวได้ทุกสเกล ·
            ครอบ/เติมพื้นหลังทำในเครื่องทันที ไม่เสียเครดิต
          </MDTypography>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                <MDTypography variant='h6'>
                  รูปต้นทาง · {sources.length}/{MAX_SOURCES}
                </MDTypography>
                <MDBox flexGrow={1} />
                <input
                  ref={fileRef}
                  type='file'
                  accept='image/*'
                  multiple
                  hidden
                  onChange={onFiles}
                  aria-label='อัปโหลดรูป'
                />
                <MDButton
                  size='small'
                  variant='gradient'
                  color='info'
                  disabled={loadingSrc || busy}
                  onClick={() => fileRef.current && fileRef.current.click()}
                >
                  <Icon>upload</Icon>&nbsp;อัปโหลดรูป
                </MDButton>
                <MDButton
                  size='small'
                  variant='outlined'
                  color='dark'
                  disabled={loadingSrc || busy}
                  onClick={() => setPickerOpen(true)}
                >
                  <Icon>collections</Icon>&nbsp;จากระบบ
                </MDButton>
              </MDBox>

              {sources.length > 0 && (
                <MDBox display='flex' gap={1} mt={1.5} sx={{ overflowX: 'auto', pb: 0.5 }}>
                  {sources.map((s) => (
                    <MDBox key={s.id} sx={{ position: 'relative', flex: '0 0 auto' }}>
                      <MDBox
                        component='button'
                        type='button'
                        aria-label={`ดูรูป ${s.name}`}
                        aria-pressed={current && current.id === s.id}
                        onClick={() => setCurrentId(s.id)}
                        sx={{
                          p: 0,
                          border: '2px solid',
                          borderColor: current && current.id === s.id ? 'info.main' : 'grey.300',
                          borderRadius: 1,
                          overflow: 'hidden',
                          width: 72,
                          height: 72,
                          cursor: 'pointer',
                          bgcolor: 'grey.100',
                          display: 'block',
                        }}
                      >
                        <MDBox
                          component='img'
                          src={s.objectUrl}
                          alt=''
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </MDBox>
                      {s.from && (
                        <MDBox
                          sx={{
                            position: 'absolute',
                            left: 2,
                            bottom: 2,
                            px: 0.5,
                            borderRadius: 0.5,
                            bgcolor: 'rgba(41,52,122,.85)',
                            color: '#fff',
                            fontSize: 10,
                            lineHeight: '14px',
                          }}
                          title={saveLabel(s.from)}
                        >
                          {s.from.code}
                        </MDBox>
                      )}
                      <IconButton
                        size='small'
                        aria-label={`เอารูป ${s.name} ออก`}
                        disabled={busy}
                        onClick={() => removeSource(s.id)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: '#fff',
                          boxShadow: 1,
                          p: 0.25,
                          '&:hover': { bgcolor: '#fff' },
                        }}
                      >
                        <Icon fontSize='small'>close</Icon>
                      </IconButton>
                    </MDBox>
                  ))}
                </MDBox>
              )}

              {!current ? (
                <MDBox
                  mt={2}
                  p={4}
                  textAlign='center'
                  sx={{ border: '1px dashed', borderColor: 'grey.400', borderRadius: 2 }}
                >
                  <Icon sx={{ fontSize: '40px !important', color: 'grey.500' }}>
                    add_photo_alternate
                  </Icon>
                  <MDTypography variant='button' color='text' display='block'>
                    {loadingSrc ? 'กำลังเปิดรูป…' : 'อัปโหลดรูป หรือเลือกจากระบบ (ได้หลายรูป)'}
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox mt={2} display='flex' gap={2} flexWrap='wrap'>
                  <MDBox flex='1 1 320px' minWidth={0}>
                    <MDTypography variant='caption' fontWeight='bold' display='block' mb={0.5}>
                      {current.name} · {current.width}×{current.height}
                      {mode === 'crop' ? ' · กดหรือลากเพื่อเลือกส่วนที่จะเก็บ' : ''}
                      {mode === 'ai' ? ' · AI จะต่อฉากรอบ ๆ ให้เต็มสเกล (ภาพเดิมอยู่ครบ)' : ''}
                    </MDTypography>
                    {/* กรอบครอบ: กด/ลาก หรือใช้ลูกศรเลื่อนจุดโฟกัสทีละ 2% */}
                    <MDBox
                      ref={boxRef}
                      role='button'
                      tabIndex={0}
                      aria-label='เลือกส่วนที่จะเก็บ กดหรือลากบนภาพ หรือใช้ปุ่มลูกศร'
                      onPointerDown={onPointerDown}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerUp}
                      onPointerCancel={onPointerUp}
                      onKeyDown={nudgeFocus}
                      sx={{
                        position: 'relative',
                        lineHeight: 0,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        overflow: 'hidden',
                        userSelect: 'none',
                        touchAction: 'none',
                        cursor: mode === 'crop' ? 'crosshair' : 'default',
                      }}
                    >
                      <img
                        src={current.objectUrl}
                        alt=''
                        style={{ width: '100%', display: 'block' }}
                        draggable={false}
                      />
                      {crop && (
                        <MDBox
                          sx={{
                            position: 'absolute',
                            left: `${(crop.x / current.width) * 100}%`,
                            top: `${(crop.y / current.height) * 100}%`,
                            width: `${(crop.w / current.width) * 100}%`,
                            height: `${(crop.h / current.height) * 100}%`,
                            outline: '2px solid #fff',
                            boxShadow: '0 0 0 9999px rgba(0,0,0,.45)',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </MDBox>
                  </MDBox>
                  {mode !== 'ai' && (
                    <MDBox flex='0 1 240px' minWidth={0}>
                      <MDTypography variant='caption' fontWeight='bold' display='block' mb={0.5}>
                        ตัวอย่าง {previewRatio.key} {mode === 'crop' ? '(ครอบ)' : '(เติมพื้นหลัง)'}
                      </MDTypography>
                      <canvas
                        ref={previewRef}
                        style={{
                          width: '100%',
                          display: 'block',
                          borderRadius: 4,
                          border: '1px solid #ddd',
                        }}
                        aria-label='ตัวอย่างผลลัพธ์'
                      />
                    </MDBox>
                  )}
                </MDBox>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, position: { md: 'sticky' }, top: 96 }}>
              <MDTypography variant='h6' mb={1}>
                สเกลที่ต้องการ · ติ๊กได้หลายแบบ
              </MDTypography>
              <MDBox display='flex' flexWrap='wrap' gap={0.75}>
                {RATIOS.map((r) => {
                  const off = mode === 'ai' && !aiRatios.includes(r.key);
                  return (
                    <Chip
                      key={r.key}
                      label={r.key}
                      title={off ? 'AI ไม่รองรับสเกลนี้ — ใช้ครอบ/เติมพื้นหลัง' : r.label}
                      color={ratios.includes(r.key) && !off ? 'info' : 'default'}
                      variant={ratios.includes(r.key) && !off ? 'filled' : 'outlined'}
                      disabled={off || busy}
                      onClick={() => toggleRatio(r.key)}
                      aria-pressed={ratios.includes(r.key) && !off}
                    />
                  );
                })}
              </MDBox>

              <MDTypography variant='h6' mt={2} mb={1}>
                วิธีแปลง
              </MDTypography>
              <MDBox display='flex' gap={1} flexWrap='wrap'>
                <MDButton
                  size='small'
                  variant={mode === 'crop' ? 'gradient' : 'outlined'}
                  color='dark'
                  disabled={busy}
                  onClick={() => changeMode('crop')}
                  aria-pressed={mode === 'crop'}
                  sx={{ flex: 1 }}
                >
                  ครอบ
                </MDButton>
                <MDButton
                  size='small'
                  variant={mode === 'fit' ? 'gradient' : 'outlined'}
                  color='dark'
                  disabled={busy}
                  onClick={() => changeMode('fit')}
                  aria-pressed={mode === 'fit'}
                  sx={{ flex: 1 }}
                >
                  เติมพื้นหลัง
                </MDButton>
                {aiOn && (
                  <MDButton
                    size='small'
                    variant={mode === 'ai' ? 'gradient' : 'outlined'}
                    color='info'
                    disabled={busy}
                    onClick={() => changeMode('ai')}
                    aria-pressed={mode === 'ai'}
                    sx={{ flex: '1 1 100%' }}
                  >
                    <Icon>auto_awesome</Icon>&nbsp;ให้ AI ต่อภาพ (ใช้เครดิต)
                  </MDButton>
                )}
              </MDBox>
              <MDTypography variant='caption' color='text' display='block' mt={0.5}>
                {mode === 'crop' && 'ตัดส่วนที่เกินออก เลือกจุดที่จะเก็บได้บนภาพ'}
                {mode === 'fit' && 'ภาพเดิมอยู่ครบ เติมขอบให้ได้สเกล'}
                {mode === 'ai' &&
                  'AI ต่อฉากรอบ ๆ ให้เต็มสเกล คงสินค้า/ข้อความเดิม · รอ 2–5 นาทีต่อภาพ · ตรวจผลก่อนใช้ทุกครั้ง'}
              </MDTypography>
              {mode === 'fit' && (
                <MDBox display='flex' gap={0.75} mt={1} alignItems='center' flexWrap='wrap'>
                  {[
                    ['white', 'ขาว'],
                    ['color', 'สีเลือกเอง'],
                    ['blur', 'เบลอจากภาพ'],
                  ].map(([k, label]) => (
                    <Chip
                      key={k}
                      size='small'
                      label={label}
                      color={bg === k ? 'info' : 'default'}
                      variant={bg === k ? 'filled' : 'outlined'}
                      onClick={() => changeBg(k)}
                      disabled={busy}
                      aria-pressed={bg === k}
                    />
                  ))}
                  {bg === 'color' && (
                    <input
                      type='color'
                      value={color}
                      disabled={busy}
                      onChange={(e) => changeColor(e.target.value)}
                      aria-label='สีพื้นหลัง'
                      style={{
                        width: 36,
                        height: 28,
                        border: 'none',
                        background: 'none',
                        padding: 0,
                      }}
                    />
                  )}
                </MDBox>
              )}

              {mode !== 'ai' ? (
                <MDBox display='flex' gap={1} mt={2}>
                  <MDInput
                    select
                    size='small'
                    label='ขนาดผลลัพธ์'
                    value={longSide}
                    onChange={(e) => changeLongSide(e.target.value)}
                    sx={{ flex: 1 }}
                    InputProps={{ disabled: busy }}
                    InputLabelProps={{ shrink: true }}
                  >
                    {LONG_SIDES.map((l) => (
                      <MenuItem key={l.key} value={l.key}>
                        {l.label}
                      </MenuItem>
                    ))}
                  </MDInput>
                  <MDInput
                    select
                    size='small'
                    label='ไฟล์'
                    value={format}
                    onChange={(e) => changeFormat(e.target.value)}
                    sx={{ width: 96 }}
                    InputProps={{ disabled: busy }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value='jpg'>JPG</MenuItem>
                    <MenuItem value='png'>PNG</MenuItem>
                  </MDInput>
                </MDBox>
              ) : (
                <MDBox display='flex' gap={1} mt={2}>
                  {(ai.providers || []).length > 1 && (
                    <MDInput
                      select
                      size='small'
                      label='โมเดล'
                      value={provider}
                      onChange={(e) => setProvider(e.target.value)}
                      sx={{ flex: 1 }}
                      InputLabelProps={{ shrink: true }}
                    >
                      {ai.providers.map((p) => (
                        <MenuItem key={p.code} value={p.code}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </MDInput>
                  )}
                  <MDInput
                    select
                    size='small'
                    label='ความละเอียด'
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    sx={{ width: 150 }}
                    InputLabelProps={{ shrink: true }}
                  >
                    {(ai.resolutions || ['1K', '2K']).map((r) => (
                      <MenuItem key={r} value={r}>
                        {r} · {creditOf(providerRow, r)} เครดิต
                      </MenuItem>
                    ))}
                  </MDInput>
                </MDBox>
              )}

              {mode !== 'ai' ? (
                <MDButton
                  variant='gradient'
                  color='info'
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={!total || busy}
                  onClick={convert}
                >
                  <Icon>auto_fix_high</Icon>&nbsp;
                  {progress
                    ? `กำลังแปลง ${progress[0]}/${progress[1]}`
                    : `แปลงภาพ · ${sources.length} รูป × ${selectedRatios.length} สเกล = ${total} ภาพ`}
                </MDButton>
              ) : (
                <>
                  <MDButton
                    variant='gradient'
                    color='info'
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={!aiCount || busy || ai.ai_off === 'Y' || pendingAi + aiCount > 24}
                    onClick={() => setAiConfirm(true)}
                  >
                    <Icon>auto_awesome</Icon>&nbsp;
                    {aiSubmitting
                      ? 'กำลังสั่งงาน…'
                      : `สั่ง AI ต่อภาพ · ${aiCount} งาน ≈ ${aiCredits} เครดิต`}
                  </MDButton>
                  {mode === 'ai' && ratios.length > 0 && !selectedRatios.length && (
                    <MDTypography variant='caption' color='warning' display='block' mt={0.5}>
                      สเกลที่ติ๊กไว้ AI ไม่รองรับ (กระดาษ A3/A4/A5) — เลือกสเกลอื่นเพิ่ม
                    </MDTypography>
                  )}
                  {ai.ai_off === 'Y' && (
                    <MDTypography variant='caption' color='error' display='block' mt={0.5}>
                      AI ปิดทั้งร้านอยู่ (โควตาเป็น 0 / หยุดฉุกเฉิน) — ผู้ดูแลระบบเปิดได้ที่หน้า AI
                      ค่าใช้จ่าย
                    </MDTypography>
                  )}
                  {pendingAi + aiCount > 24 && (
                    <MDTypography variant='caption' color='warning' display='block' mt={0.5}>
                      มีงานค้าง {pendingAi} งาน — รอให้เสร็จก่อนแล้วค่อยสั่งเพิ่ม
                    </MDTypography>
                  )}
                  {aiTodo > aiCount && (
                    <MDTypography variant='caption' color='warning' display='block' mt={0.5}>
                      สั่งได้ครั้งละไม่เกิน {aiCount} งาน — ที่เหลืออีก {aiTodo - aiCount} งาน
                      กดสั่งอีกรอบ
                    </MDTypography>
                  )}
                  {total > 0 && aiTodo === 0 && (
                    <MDTypography variant='caption' color='text' display='block' mt={0.5}>
                      สั่งครบทุกรูป × สเกลที่เลือกแล้ว — ดูผลที่การ์ดงาน AI ด้านล่าง ·{' '}
                      <MDTypography
                        component='button'
                        type='button'
                        variant='caption'
                        color='info'
                        onClick={() => setReorderFrom(Date.now())}
                        sx={{
                          border: 0,
                          bgcolor: 'transparent',
                          p: 0,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        อยากได้ภาพใหม่ สั่งชุดนี้อีกรอบ
                      </MDTypography>
                    </MDTypography>
                  )}
                  {ai.usage && (
                    <MDTypography variant='caption' color='text' display='block' mt={0.5}>
                      เดือนนี้ทั้งร้านใช้ไป {ai.usage.month_images} ภาพ · คุณใช้{' '}
                      {ai.usage.my_images} ภาพ
                    </MDTypography>
                  )}
                </>
              )}
              {progress && (
                <MDProgress
                  value={Math.round((progress[0] / Math.max(1, progress[1])) * 100)}
                  color='info'
                />
              )}
            </Card>
          </Grid>

          {results.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 2 }}>
                <MDBox
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'
                  flexWrap='wrap'
                  gap={1}
                >
                  <MDTypography variant='h6'>ผลลัพธ์ · {results.length} ภาพ</MDTypography>
                  <MDButton size='small' variant='gradient' color='dark' onClick={downloadAll}>
                    <Icon>download</Icon>&nbsp;ดาวน์โหลดทั้งหมด (zip)
                  </MDButton>
                </MDBox>
                <Grid container spacing={2} mt={0.5}>
                  {results.map((r) => {
                    const from = fromOf(r.sourceId);
                    return (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={r.key}>
                        <MDBox
                          sx={{
                            border: '1px solid',
                            borderColor: 'grey.300',
                            borderRadius: 2,
                            overflow: 'hidden',
                          }}
                        >
                          <MDBox
                            sx={{
                              height: 150,
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <MDBox
                              component='img'
                              src={r.url}
                              alt={`${r.name} ${r.ratioKey}`}
                              sx={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }}
                            />
                          </MDBox>
                          <MDBox p={1} display='flex' alignItems='center' gap={0.5}>
                            <MDBox minWidth={0} flexGrow={1}>
                              <MDTypography
                                variant='button'
                                fontWeight='medium'
                                display='block'
                                noWrap
                                title={fileNameOf(r)}
                              >
                                {r.ratioKey}
                              </MDTypography>
                              <MDTypography variant='caption' color='text' display='block' noWrap>
                                {r.w}×{r.h} · {r.name}
                              </MDTypography>
                              {r.up >= 1.5 ? (
                                <MDTypography
                                  variant='caption'
                                  color='warning'
                                  fontWeight='medium'
                                  display='block'
                                  noWrap
                                  title='รูปต้นทางเล็กกว่าขนาดผลลัพธ์ ระบบขยายให้ พิมพ์ออกมาอาจไม่คม · ใช้รูปที่ใหญ่กว่านี้ถ้ามี'
                                >
                                  ขยาย {r.up.toFixed(1)} เท่า
                                </MDTypography>
                              ) : null}
                            </MDBox>
                            {from && canSave && (
                              <IconButton
                                size='small'
                                aria-label={`${saveLabel(from)} (${r.ratioKey})`}
                                title={saveLabel(from)}
                                disabled={Boolean(saving) || Boolean(savedKeys[r.key])}
                                onClick={() =>
                                  saveBack(r.key, from, fileNameOf(r), async () => ({
                                    filedata: await blobToDataUrl(r.blob),
                                    blob: r.blob,
                                  }))
                                }
                              >
                                {saving === r.key ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Icon
                                    fontSize='small'
                                    color={savedKeys[r.key] ? 'success' : 'inherit'}
                                  >
                                    {savedKeys[r.key] ? 'check_circle' : 'save'}
                                  </Icon>
                                )}
                              </IconButton>
                            )}
                            <IconButton
                              size='small'
                              aria-label={`ดาวน์โหลด ${fileNameOf(r)}`}
                              onClick={() => downloadOne(r)}
                            >
                              <Icon fontSize='small'>download</Icon>
                            </IconButton>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    );
                  })}
                </Grid>
              </Card>
            </Grid>
          )}

          {aiJobs.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 2 }}>
                <MDBox
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'
                  flexWrap='wrap'
                  gap={1}
                >
                  <MDTypography variant='h6'>
                    งาน AI · {aiJobs.length} งาน
                    {pendingAi ? ` · กำลังทำ ${pendingAi} (รอ 2–5 นาทีต่อภาพ)` : ''}
                  </MDTypography>
                  <MDButton
                    size='small'
                    variant='gradient'
                    color='dark'
                    disabled={!aiJobs.some((j) => j.state === 'SUCCESS')}
                    onClick={downloadAiAll}
                  >
                    <Icon>download</Icon>&nbsp;ดาวน์โหลดที่เสร็จ (zip)
                  </MDButton>
                </MDBox>
                <Grid container spacing={2} mt={0.5}>
                  {aiJobs.map((j) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={j.id}>
                      <MDBox
                        sx={{
                          border: '1px solid',
                          borderColor: j.state === 'FAILED' ? 'error.main' : 'grey.300',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <MDBox
                          sx={{
                            height: 150,
                            bgcolor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {j.state === 'SUCCESS' && (
                            <MDBox
                              component='img'
                              src={j.url}
                              alt={`${j.name} ${j.ratioKey} AI`}
                              sx={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }}
                            />
                          )}
                          {j.state === 'FAILED' && (
                            <>
                              <Icon color='error'>error_outline</Icon>
                              <MDTypography
                                variant='caption'
                                color='error'
                                textAlign='center'
                                px={1}
                              >
                                ไม่สำเร็จ{j.error ? ` · ${j.error}` : ''}
                              </MDTypography>
                            </>
                          )}
                          {j.state !== 'SUCCESS' && j.state !== 'FAILED' && (
                            <>
                              <CircularProgress size={22} />
                              <MDTypography variant='caption' color='text'>
                                AI กำลังต่อภาพ…
                              </MDTypography>
                            </>
                          )}
                        </MDBox>
                        <MDBox p={1} display='flex' alignItems='center' gap={0.5}>
                          <MDBox minWidth={0} flexGrow={1}>
                            <MDTypography
                              variant='button'
                              fontWeight='medium'
                              display='block'
                              noWrap
                            >
                              {j.ratioKey} · AI
                            </MDTypography>
                            <MDTypography variant='caption' color='text' display='block' noWrap>
                              {j.state === 'SUCCESS' && j.w ? `${j.w}×${j.h} · ` : ''}
                              {j.state === 'SUCCESS' ? `${j.credits} เครดิต · ` : ''}
                              {j.name}
                              {j.at ? ` · ${String(j.at).slice(0, 16)}` : ''}
                            </MDTypography>
                          </MDBox>
                          {j.state === 'SUCCESS' && j.from && canSave && (
                            <IconButton
                              size='small'
                              aria-label={`${saveLabel(j.from)} (AI ${j.ratioKey})`}
                              title={saveLabel(j.from)}
                              disabled={Boolean(saving) || Boolean(savedKeys[`ai${j.id}`])}
                              onClick={() =>
                                saveBack(`ai${j.id}`, j.from, aiFileName(j), async () => ({
                                  gen_id: j.id, // คัดลอกไฟล์ฝั่ง server ไม่ต้องส่งไฟล์ใหญ่
                                }))
                              }
                            >
                              {saving === `ai${j.id}` ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Icon
                                  fontSize='small'
                                  color={savedKeys[`ai${j.id}`] ? 'success' : 'inherit'}
                                >
                                  {savedKeys[`ai${j.id}`] ? 'check_circle' : 'save'}
                                </Icon>
                              )}
                            </IconButton>
                          )}
                          {j.state === 'SUCCESS' && (
                            <IconButton
                              size='small'
                              aria-label={`ดาวน์โหลด ${aiFileName(j)}`}
                              onClick={() => downloadAiOne(j)}
                            >
                              <Icon fontSize='small'>download</Icon>
                            </IconButton>
                          )}
                        </MDBox>
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>

      {/* ยืนยันก่อนใช้เครดิต */}
      <Dialog open={aiConfirm} onClose={() => setAiConfirm(false)} fullWidth maxWidth='xs'>
        <DialogTitle>สั่ง AI ต่อภาพ?</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2'>
            {aiCount} งาน{aiTodo > aiCount ? ` (จากที่ยังไม่ได้สั่ง ${aiTodo})` : ''} · ≈{' '}
            {aiCredits} เครดิต ({creditEach} ต่อภาพที่ {resolution})
          </MDTypography>
          <MDTypography variant='body2' mt={1}>
            AI จะต่อฉากรอบ ๆ ให้เต็มสเกลโดยคงสินค้าและข้อความเดิม · รอ 2–5 นาทีต่อภาพ ·
            ภาพที่ไม่สำเร็จไม่หักเครดิต
          </MDTypography>
          <MDTypography variant='caption' color='text' display='block' mt={1}>
            ตรวจผลก่อนใช้ทุกครั้ง — AI อาจวาดตัวอักษรเพี้ยนได้
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            autoFocus
            variant='outlined'
            color='secondary'
            onClick={() => setAiConfirm(false)}
          >
            ยกเลิก
          </MDButton>
          <MDButton variant='gradient' color='info' onClick={submitAi}>
            สั่ง AI · {aiCredits} เครดิต
          </MDButton>
        </DialogActions>
      </Dialog>

      <SystemImagePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(items) => addSources(items)}
        token={app.token}
      />
      {notify}
      <Footer />
    </DashboardLayout>
  );
}

export default ImageScale;
